import React from "react";
import { useReveal } from "../hooks/useReveal";

const BG2 = "/assets/bg2.png";

const NOTICED = [
  "Less distracted by his phone",
  "More focused during workouts",
  "More creative",
  "More present",
  "More intentional under pressure",
];

export default function HowItStarted() {
  const headRef = useReveal();
  const bodyRef = useReveal();
  return (
    <section
      id="origin"
      data-testid="origin-section"
      className="section relative overflow-hidden bg-[#020305]"
    >
      <img
        src={BG2}
        alt=""
        aria-hidden
        className="hidden lg:block pointer-events-none absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-screen"
      />

      <div className="container-x relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-20">
        <div ref={headRef} className="reveal lg:col-span-5 lg:sticky lg:top-32 self-start">
          <span className="font-mono text-sm uppercase tracking-[0.3em] text-white/55">
            / 04 — Origin
          </span>
          <h2
            className="kinetic mt-6"
            style={{ fontSize: "clamp(2.4rem, 5.5vw, 5rem)", lineHeight: 0.95 }}
          >
            <span className="gradient-text">HOW THIS</span>
            <br />
            <span className="outline-text-blue">STARTED.</span>
          </h2>
          <div className="mt-8 font-mono text-sm uppercase tracking-[0.25em] text-white/40">
            Sept 2025 · Founder's Note
          </div>

          {/* Origin visual — training environment */}
          <div
            data-testid="origin-visual"
            className="mt-10 relative rounded-2xl overflow-hidden border border-white/8 aspect-[4/3]"
          >
            <img
              src="/assets/gym-rim.jpg"
              alt="Training environment"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover grayscale-[30%] contrast-110"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(2,3,5,0.15) 0%, rgba(2,3,5,0.85) 100%)",
              }}
            />
            <div
              className="absolute inset-0 mix-blend-overlay opacity-70"
              style={{
                background:
                  "radial-gradient(circle at 20% 80%, rgba(31,179,255,0.4), transparent 55%)",
              }}
            />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                  Where it began
                </div>
                <div className="font-display text-base uppercase text-white mt-1 leading-tight">
                  A hard chew. <br/>An unexpected anchor.
                </div>
              </div>
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{
                  background: "var(--blue-bright)",
                  boxShadow: "0 0 12px var(--blue-bright)",
                }}
              />
            </div>
          </div>
        </div>

        <div ref={bodyRef} className="reveal lg:col-span-7 lg:col-start-6 space-y-8 text-white/75 text-lg leading-relaxed max-w-2xl">
          <p>
            In September 2025, founder{" "}
            <span className="text-white font-medium">Jeramy Poole</span> began chewing an
            extremely hard, flavorless gum made by a small private company in Turkey.
          </p>
          <p>At first, the reason was purely aesthetic.</p>
          <p>
            Jeramy noticed his face was starting to soften on camera and liked the idea of
            toning his face while also training his body in the gym.
          </p>
          <p
            className="text-2xl lg:text-3xl text-white font-light leading-snug py-4 border-l-2 pl-6"
            style={{ borderColor: "var(--blue-bright)" }}
          >
            But something unexpected happened.
          </p>
          <p>
            Because the gum required real resistance to chew, it occupied just enough
            attention to keep him grounded.
          </p>
          <p>He noticed he was:</p>

          <ul className="grid sm:grid-cols-2 gap-4 not-prose">
            {NOTICED.map((n, i) => (
              <li
                key={i}
                data-testid={`origin-noticed-${i}`}
                className="flex items-center gap-3 p-4 card-dark"
              >
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: "var(--blue-bright)", boxShadow: "0 0 12px var(--blue-bright)" }}
                />
                <span className="text-white/90 text-base">{n}</span>
              </li>
            ))}
          </ul>

          <p>Many of his best ideas started showing up while he was training.</p>
          <p>
            Soon, Jeramy began reaching for hard gum not only during workouts, but before
            client projects, creative work, strategy sessions, filming, and moments where
            he needed deeper focus and more intentional energy.
          </p>

          <div className="pt-6">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/45 mb-3">
              The question that became Protos
            </p>
            <p
              className="kinetic"
              style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.8rem)", lineHeight: 1 }}
            >
              <span className="gradient-text">What if this simple behavior</span>
              <br />
              <span className="outline-text-blue">could be engineered into a product?</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
